import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditCharityModal from "./EditCharityModal";
import ConfirmModal from "../form-components/ConfirmModal";
import { deleteCharity } from "../../api/charityApi";
import { submitApproval } from "../../api/approvalApi";
import { useLocalization } from "../../context/LocalizationContext";
import { useToast } from "../../context/ToastContext";
import { usePermissions } from "../../hooks/usePermissions";
import LocalText from "../common/LocalText";
import ApprovalStatusBadge from "../approval/ApprovalStatusBadge";
import SubmitForApprovalButton from "../approval/SubmitForApprovalButton";
import WithdrawButton from "../approval/WithdrawButton";
import ContentPreviewDrawer from "../approval/ContentPreviewDrawer";
import { CONTENT_TYPES } from '../../constants/contentTypes';

const viewBtnSx = {
  borderColor: '#3b82f6',
  color: '#1d4ed8',
  bgcolor: '#eff6ff',
  borderRadius: '8px',
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb', boxShadow: 'none' },
};

const CharityCard = ({
  localizedName,
  websiteUrl,
  localizedDescription,
  donationUrl,
  isActive,
  media,
  id,
  status,
  contentStatus,
  onSuccess,
  approvalStatus,
  approvalId,
  approvalActionType,
  _viewType,
  _liveId,
  preview = false,
  overrideSpaceId,
}) => {
  const [open, setOpen] = useState(false);
  const [editReadOnly, setEditReadOnly] = useState(false);
  const [editApprovalMode, setEditApprovalMode] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [proposeDeleteOpen, setProposeDeleteOpen] = useState(false);
  const [viewProposalOpen, setViewProposalOpen] = useState(false);
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [resubmitInitialData, setResubmitInitialData] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { locale, t } = useLocalization();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/charities', 'charity-edit');

  const isSnapshot    = _viewType === 'snapshot';
  const isLocked      = approvalStatus === 'pending' || approvalStatus === 'rejected';
  const canDirectEdit = contentStatus === 'draft' && !isLocked;

  const charityName =
    localizedName?.values?.find((v) => v.localeId === locale)?.value ||
    localizedName?.values?.[0]?.value ||
    t(localizedName?.key, '') ||
    '';

  const image =
    media?.linkLocalizations?.find((m) => m.localeId === locale)?.mediaLink || null;

  const shouldShowPlaceholder = !image || imageError;

  const viewApprovalRecord = approvalId ? {
    approvalId:       approvalId,
    actionType:       approvalActionType,
    contentType:      CONTENT_TYPES.CHARITY,
    sectionLabel:     'Charity',
    contentName:      charityName,
    submittedByName: '',
    submittedAt:      null,
    status:            approvalStatus,
  } : null;

  const handleDeleteConfirm = async () => {
    try {
      await deleteCharity(id);
      toast.success("Charity deleted successfully");
      setDeleteModalOpen(false);
      onSuccess?.();
    } catch (err) {
      console.error("Failed to delete charity:", err);
      // error shown globally via axios interceptor → toast
    }
  };

  const handleProposeDelete = async () => {
    try {
      await submitApproval({
        contentType: CONTENT_TYPES.CHARITY,
        contentId: id,
        contentName: charityName,
        actionType: 'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteOpen(false);
      onSuccess?.('pending');
    } catch {
      // error shown globally via axios interceptor → toast
    }
  };

  const handleCloseEditModal = () => {
    setOpen(false);
    setEditReadOnly(false);
    setEditApprovalMode(false);
  };

  const deleteTooltip = isLocked
    ? approvalStatus === 'pending'
      ? 'A pending proposal exists — Withdraw it first'
      : 'A rejected proposal exists — Dismiss it first'
    : canDirectEdit
      ? 'Delete charity'
      : 'Propose deletion (requires approval)';

  return (
    <Card
      sx={{
        width: "100%",
        bgcolor: "white",
        borderRadius: "16px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "2px solid transparent",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "all 0.2s ease",
        ...(!preview && {
          "&:hover": {
            boxShadow: "0 8px 24px rgba(10,50,109,0.15)",
            borderColor: "#5383b3",
            transform: "translateY(-2px)",
          },
        }),
      }}
    >
      {/* Snapshot version banner */}
      {isSnapshot && (
        <Box sx={{
          bgcolor: approvalStatus === 'pending' ? '#d97706' : '#dc2626',
          color: 'white', px: 1.5, py: 0.5, fontSize: 11, fontWeight: 600, textAlign: 'center',
        }}>
          {approvalStatus === 'pending' ? 'Proposed — Pending Review' : 'Proposed — Rejected'}
        </Box>
      )}

      {/* ── Image ── */}
      <Box
        sx={{
          height: 140,
          position: "relative",
          bgcolor: "#e2e8f0",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {!shouldShowPlaceholder && !imageLoaded && !imageError && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={28} sx={{ color: "#5383b3" }} />
          </Box>
        )}

        {!shouldShowPlaceholder && (
          <Box
            component="img"
            src={image}
            alt={charityName}
            sx={{ width: "100%", height: "100%", objectFit: "cover", opacity: imageLoaded ? 1 : 0, transition: "opacity 0.4s ease-in-out" }}
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageError(true)}
          />
        )}

        {/* Active/Inactive badge — hidden for now
        {!isSnapshot && (
          <Chip
            label={<LocalText>{isActive ? "Active" : "Inactive"}</LocalText>}
            size="small"
            sx={{
              position: "absolute", top: 8, right: 8,
              height: 22, fontSize: 10, fontWeight: 700,
              bgcolor: isActive ? "#dcfce7" : "#f1f5f9",
              color: isActive ? "#15803d" : "#64748b",
              border: `1px solid ${isActive ? "#bbf7d0" : "#e2e8f0"}`,
            }}
          />
        )}
        */}
      </Box>

      {/* ── Content ── */}
      <CardContent sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography fontWeight={700} fontSize={13.5} sx={{ color: "#1e293b", lineHeight: 1.35 }} noWrap>
          {charityName}
        </Typography>

        <Typography
          variant="caption"
          sx={{ color: "#64748b", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {websiteUrl || <LocalText>No website</LocalText>}
        </Typography>

        <Typography
          fontSize={11.5}
          sx={{ color: "#64748b", lineHeight: 1.55, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", flex: 1 }}
        >
          {localizedDescription?.values?.find((v) => v.localeId === locale)?.value ||
           localizedDescription?.values?.[0]?.value ||
           t(localizedDescription?.key, '') ||
           ''}
        </Typography>

        {donationUrl && (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "#EAF0F6", borderRadius: "8px", px: 1, py: 0.5, cursor: preview ? "default" : "pointer" }}
            onClick={preview ? undefined : () => window.open(donationUrl, "_blank", "noopener,noreferrer")}
          >
            <LinkIcon sx={{ fontSize: 13, color: "#0a326d", flexShrink: 0 }} />
            <Typography fontSize={11} sx={{ color: "#0a326d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {donationUrl}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* ── Approval ── */}
      {!preview && (contentStatus || approvalStatus) && (
        <Box sx={{ px: 2, py: 1, borderTop: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {/* Badge: live cards show contentStatus; snapshot cards show approvalStatus */}
          {(!isSnapshot ? contentStatus : approvalStatus) && (
            <ApprovalStatusBadge
              status={!isSnapshot ? contentStatus : approvalStatus}
              actionType={approvalActionType}
            />
          )}

          {/* True draft only (no ca record): Submit for Approval */}
          {!isSnapshot && canDirectEdit && contentStatus === 'draft' && canEdit && (
            <SubmitForApprovalButton contentType={CONTENT_TYPES.CHARITY} contentId={id} contentName={charityName} onSubmitted={() => onSuccess?.('pending')} />
          )}

          {/* Snapshot: View button (pending and rejected) */}
          {isSnapshot && viewApprovalRecord && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
              onClick={() => setViewProposalOpen(true)}
              sx={viewBtnSx}
            >
              View
            </Button>
          )}

          {/* Snapshot pending: Withdraw */}
          {isSnapshot && approvalStatus === "pending" && approvalId && canEdit && (
            <WithdrawButton approvalId={approvalId} onWithdrawn={() => onSuccess?.(approvalActionType === 'create' ? 'draft' : 'live')} />
          )}

          {/* Snapshot rejected: Dismiss only (Edit & Resubmit lives inside ContentComparisonView) */}
          {isSnapshot && approvalStatus === "rejected" && approvalId && canEdit && (
            <WithdrawButton approvalId={approvalId} onWithdrawn={() => onSuccess?.(approvalActionType === 'create' ? 'draft' : 'live')} variant="dismiss" />
          )}
        </Box>
      )}

      {/* ── Actions ── */}
      {!preview && !isSnapshot && (
        <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
          {isLocked ? (
            /* Pending or rejected: show eye icon — live card is read-only */
            <Tooltip title="View live content (read-only)">
              <IconButton
                size="small"
                onClick={(e) => { e.currentTarget.blur(); setEditReadOnly(true); setEditApprovalMode(false); setOpen(true); }}
                sx={{ color: "#3b82f6", borderRadius: "8px", "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8" } }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : canEdit ? (
            /* Not locked: direct edit (draft) or propose edit (approved) */
            <Tooltip title={canDirectEdit ? 'Edit charity' : 'Propose edit (requires approval)'}>
              <IconButton
                size="small"
                onClick={(e) => { e.currentTarget.blur(); setEditReadOnly(false); setEditApprovalMode(!canDirectEdit); setOpen(true); }}
                sx={{ color: "#64748b", borderRadius: "8px", "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8" } }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            /* Read-only viewers (e.g. GMS accountant): view full details, no edit */
            <Tooltip title="View charity">
              <IconButton
                size="small"
                onClick={(e) => { e.currentTarget.blur(); setEditReadOnly(true); setEditApprovalMode(false); setOpen(true); }}
                sx={{ color: "#3b82f6", borderRadius: "8px", "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8" } }}
              >
                <VisibilityIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canEdit && (
            <Tooltip title={deleteTooltip}>
              <span>
                <IconButton
                  size="small"
                  disabled={isLocked}
                  onClick={(e) => { e.currentTarget.blur(); if (canDirectEdit) setDeleteModalOpen(true); else setProposeDeleteOpen(true); }}
                  sx={{ color: "#64748b", borderRadius: "8px", "&:hover": { bgcolor: "#fff1f2", color: "#dc2626" } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Live card edit modal (direct or propose) */}
      {!preview && !isSnapshot && (
        <EditCharityModal
          open={open}
          onClose={handleCloseEditModal}
          onSuccess={() => {
            if (!editReadOnly) {
              toast.success(editApprovalMode ? "Edit submitted for approval!" : "Charity updated successfully");
            }
            onSuccess?.(editReadOnly ? undefined : (editApprovalMode ? 'pending' : 'draft'));
          }}
          charityId={id}
          readOnly={editReadOnly}
          approvalMode={editApprovalMode}
          overrideSpaceId={overrideSpaceId}
        />
      )}

      {/* Direct delete confirm */}
      {!preview && !isSnapshot && (
        <ConfirmModal
          open={deleteModalOpen}
          onCancel={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          severity="danger"
          title="Delete Charity"
          message="Are you sure you want to delete this charity?"
          subtitle="This action cannot be undone."
          confirmLabel="Delete"
        />
      )}

      {/* Propose deletion confirm */}
      {!preview && !isSnapshot && (
        <ConfirmModal
          open={proposeDeleteOpen}
          onCancel={() => setProposeDeleteOpen(false)}
          onConfirm={handleProposeDelete}
          severity="warning"
          title="Propose Deletion"
          message={`Submit a delete request for "${charityName}"? The content will remain active until an approver approves.`}
          confirmLabel="Submit for Approval"
        />
      )}

      {/* Snapshot: resubmit after rejection — modal pre-filled with rejected proposed data */}
      {isSnapshot && (
        <EditCharityModal
          open={resubmitOpen}
          onClose={() => { setResubmitOpen(false); setResubmitInitialData(null); }}
          onSuccess={() => { setResubmitOpen(false); setResubmitInitialData(null); onSuccess?.('pending'); }}
          charityId={_liveId ?? id}
          approvalMode={true}
          overrideData={resubmitInitialData}
          overrideSpaceId={overrideSpaceId}
        />
      )}

      {/* View proposal / comparison (both live and snapshot cards) */}
      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => setViewProposalOpen(false)}
          readOnly
          onEdit={canEdit ? (proposedSnap) => {
            setViewProposalOpen(false);
            setResubmitInitialData(proposedSnap);
            setResubmitOpen(true);
          } : undefined}
        />
      )}
    </Card>
  );
};

export default CharityCard;
